import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Pocket, TrendingDown, Shield } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetPettyCashFundQuery,
  useCreatePettyCashFundMutation,
  useUpdatePettyCashFundMutation,
  usePostPettyCashAdjustmentMutation,
} from "../../pettyCashApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useGetUsersQuery } from "../../../users/usersApi";
import { useGetRolesQuery } from "../../../roles/rolesApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanManageFund, useCanAdjust } from "../../usePettyCashPermissions";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";

const money = (n) => Number(n || 0).toFixed(2);

const emptyForm = {
  branchId: "",
  fundName: "",
  fundCode: "",
  approvedFloat: "0",
  minimumBalance: "0",
  custodianUserId: "",
  supervisorUserId: "",
  approvalRoleId: "",
  currencyCode: "KES",
  openingBalance: "0",
  isActive: true,
};

// One fund per branch (2026-08-27 redesign) - this doubles as the "detail"
// view (balance figures + Adjust Balance + View Ledger) when editing, same
// single-form-is-the-detail-page convention as EmployeeForm/ProductForm,
// since Fund (unlike Voucher/Refill/Reconciliation) has a real Update
// endpoint and no approval workflow of its own.
const FundForm = () => {
  const { fundId } = useParams();
  const navigate = useNavigate();
  const canManage = useCanManageFund();
  const canAdjust = useCanAdjust();
  const isEditing = Boolean(fundId);
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const { data: users = [] } = useGetUsersQuery({});
  const { data: roles = [] } = useGetRolesQuery();
  const { data: detail, isLoading: isLoadingDetail } = useGetPettyCashFundQuery(fundId, { skip: !isEditing });
  const [createFund, { isLoading: isCreating }] = useCreatePettyCashFundMutation();
  const [updateFund, { isLoading: isUpdating }] = useUpdatePettyCashFundMutation();
  const [postAdjustment, { isLoading: isAdjusting }] = usePostPettyCashAdjustmentMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const [form, setForm] = useState(emptyForm);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  const defaultBranchId = useDefaultFormBranch(availableBranches);
  useEffect(() => {
    if (!isEditing && defaultBranchId !== "") setForm((f) => (f.branchId ? f : { ...f, branchId: defaultBranchId }));
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustDirection, setAdjustDirection] = useState("IN");
  const [adjustDescription, setAdjustDescription] = useState("");

  useEffect(() => {
    if (isEditing && detail) {
      setForm({
        ...emptyForm,
        ...detail,
        approvedFloat: String(detail.approvedFloat ?? "0"),
        minimumBalance: String(detail.minimumBalance ?? "0"),
        custodianUserId: detail.custodianUserId ?? "",
        supervisorUserId: detail.supervisorUserId ?? "",
        approvalRoleId: detail.approvalRoleId ?? "",
      });
    } else if (!isEditing) {
      setForm({
        ...emptyForm,
        branchId: defaultBranchId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, detail]);

  const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const readOnly = !canManage;

  const handleSave = async () => {
    if (!form.fundName.trim()) return toast.error("Fund name is required");
    if (!isEditing && !form.fundCode.trim()) return toast.error("Fund code is required");
    if (!isEditing && !form.branchId) return toast.error("Branch is required");
    try {
      if (isEditing) {
        await updateFund({
          fundId: Number(fundId),
          fundName: form.fundName.trim(),
          approvedFloat: Number(form.approvedFloat) || 0,
          minimumBalance: Number(form.minimumBalance) || 0,
          custodianUserId: form.custodianUserId ? Number(form.custodianUserId) : null,
          supervisorUserId: form.supervisorUserId ? Number(form.supervisorUserId) : null,
          approvalRoleId: form.approvalRoleId ? Number(form.approvalRoleId) : null,
          currencyCode: form.currencyCode || null,
          isActive: form.isActive,
        }).unwrap();
        toast.success("Fund saved");
      } else {
        const created = await createFund({
          branchId: Number(form.branchId),
          fundName: form.fundName.trim(),
          fundCode: form.fundCode.trim(),
          approvedFloat: Number(form.approvedFloat) || 0,
          minimumBalance: Number(form.minimumBalance) || 0,
          custodianUserId: form.custodianUserId ? Number(form.custodianUserId) : null,
          supervisorUserId: form.supervisorUserId ? Number(form.supervisorUserId) : null,
          approvalRoleId: form.approvalRoleId ? Number(form.approvalRoleId) : null,
          currencyCode: form.currencyCode || null,
          openingBalance: Number(form.openingBalance) || 0,
        }).unwrap();
        toast.success("Fund created");
        navigate(paths.pettyCashFundEdit(created.fundId), { replace: true });
      }
    } catch (error) {
      toast.error(error?.message || "Could not save fund");
    }
  };

  const handleAdjust = async () => {
    const amount = Number(adjustAmount);
    if (!amount || amount <= 0) return toast.error("Enter a positive amount");
    if (!adjustDescription.trim()) return toast.error("A description is required");
    try {
      await postAdjustment({
        fundId: Number(fundId),
        amount,
        isIncrease: adjustDirection === "IN",
        description: adjustDescription.trim(),
      }).unwrap();
      toast.success("Adjustment posted");
      setAdjustAmount("");
      setAdjustDescription("");
    } catch (error) {
      toast.error(error?.message || "Could not post the adjustment");
    }
  };

  if (isEditing && isLoadingDetail) {
    return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  }

  const isLow = detail && detail.currentBalance <= detail.minimumBalance;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.pettyCashFunds} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Funds
              </Link>
              <h4>{isEditing ? "Fund Details" : "Add New Fund"}</h4>
            </div>
          </div>
        </div>

        {isEditing && detail && (
          <div className="card mb-4">
            <div className="card-body d-flex align-items-center gap-3">
              <span className="master-detail-avatar master-detail-avatar-lg">
                {detail.branchCode?.slice(0, 2).toUpperCase() || "?"}
              </span>
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h5 className="mb-0">{detail.fundName}</h5>
                  <span className="badge bg-light text-dark border">{detail.fundCode}</span>
                  <span className={`badge ${isLow ? "badge-linedanger" : "badge-linesuccess"}`}>{isLow ? "Low" : "Normal"}</span>
                </div>
                <div className="d-flex flex-wrap gap-3 mt-2 text-muted small">
                  <span className="d-inline-flex align-items-center">
                    <Pocket size={14} className="me-1" />
                    Balance: {money(detail.currentBalance)} {detail.currencyCode}
                  </span>
                  <span className="d-inline-flex align-items-center">
                    <TrendingDown size={14} className="me-1" />
                    Minimum: {money(detail.minimumBalance)}
                  </span>
                  <span className="d-inline-flex align-items-center">
                    <Shield size={14} className="me-1" />
                    Approved Float: {money(detail.approvedFloat)}
                  </span>
                </div>
              </div>
              <Link to={`${paths.pettyCashLedger}?fundId=${detail.fundId}`} className="btn btn-outline-secondary btn-sm flex-shrink-0">
                View Ledger
              </Link>
            </div>
          </div>
        )}

        {readOnly && (
          <div className="alert alert-info">You have read-only access. Managing funds requires PettyCash.ManageFund.</div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Fund Details</h5>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Branch</label>
                {isEditing ? (
                  <input type="text" className="form-control" value={detail?.branchCode || ""} readOnly disabled />
                ) : (
                  <select className="form-control" value={form.branchId} onChange={setField("branchId")} disabled={readOnly}>
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Fund Name</label>
                <input type="text" className="form-control" value={form.fundName} onChange={setField("fundName")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Fund Code</label>
                <input type="text" className="form-control" value={form.fundCode} onChange={setField("fundCode")} disabled={readOnly || isEditing} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Approved Float</label>
                <input type="number" className="form-control" value={form.approvedFloat} onChange={setField("approvedFloat")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Minimum Balance</label>
                <input type="number" className="form-control" value={form.minimumBalance} onChange={setField("minimumBalance")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Currency Code</label>
                <input type="text" className="form-control" value={form.currencyCode} onChange={setField("currencyCode")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Custodian</label>
                <select className="form-control" value={form.custodianUserId} onChange={setField("custodianUserId")} disabled={readOnly}>
                  <option value="">--None--</option>
                  {users.map((u) => (
                    <option key={u.userId} value={u.userId}>{u.fullName || u.username}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Supervisor</label>
                <select className="form-control" value={form.supervisorUserId} onChange={setField("supervisorUserId")} disabled={readOnly}>
                  <option value="">--None--</option>
                  {users.map((u) => (
                    <option key={u.userId} value={u.userId}>{u.fullName || u.username}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Approval Role</label>
                <select className="form-control" value={form.approvalRoleId} onChange={setField("approvalRoleId")} disabled={readOnly}>
                  <option value="">--None--</option>
                  {roles.map((r) => (
                    <option key={r.roleId} value={r.roleId}>{r.roleName}</option>
                  ))}
                </select>
              </div>
              {!isEditing && (
                <div className="col-md-4 mb-3">
                  <label className="form-label">Opening Balance</label>
                  <input type="number" className="form-control" value={form.openingBalance} onChange={setField("openingBalance")} disabled={readOnly} />
                </div>
              )}
              {isEditing && (
                <div className="col-md-4 mb-3 d-flex align-items-end">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="fund-active"
                      checked={form.isActive}
                      onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                      disabled={readOnly}
                    />
                    <label className="form-check-label" htmlFor="fund-active">Active</label>
                  </div>
                </div>
              )}
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isCreating || isUpdating}>
                  Save
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.pettyCashFunds)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {isEditing && canAdjust && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">Adjust Balance</h5>
              <div className="row align-items-end">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Direction</label>
                  <select className="form-control" value={adjustDirection} onChange={(e) => setAdjustDirection(e.target.value)}>
                    <option value="IN">Increase</option>
                    <option value="OUT">Decrease</option>
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Amount</label>
                  <input type="number" className="form-control" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Description</label>
                  <input type="text" className="form-control" value={adjustDescription} onChange={(e) => setAdjustDescription(e.target.value)} />
                </div>
              </div>
              <button type="button" className="btn btn-outline-primary" onClick={handleAdjust} disabled={isAdjusting}>
                Post Adjustment
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundForm;
